import { useCallback, useEffect, useReducer, useState } from 'react';

import api, { ApiParams, FetchError, FetchMethod, FetchRequest } from './api';

export interface ApiResult<T> {
  data?: T;
  isLoading: boolean;
  response?: Response;
  error?: FetchError;
}

export type FetchApiCallback = (params?: ApiParams) => void;

export interface ApiFetchResult<T> extends ApiResult<T> {
  fetchApi: FetchApiCallback;
  clearData: () => void;
}

export interface ApiGetResult<T> extends ApiResult<T> {
  get: FetchApiCallback;
  clearData: () => void;
}

export interface ApiPatchResult<T> extends ApiResult<T> {
  patch: FetchApiCallback;
  clearData: () => void;
}

export interface ApiPostResult<T> extends ApiResult<T> {
  clearData: () => void;
  post: FetchApiCallback;
}

export interface ApiPutResult<T> extends ApiResult<T> {
  clearData: () => void;
  put: FetchApiCallback;
}

export interface ApiDeleteResult<T> extends ApiResult<T> {
  del: FetchApiCallback;
  clearData: () => void;
}

export function useApiGet<T>(path: string): ApiGetResult<T> {
  const { fetchApi, ...params } = useApiFetch<T>(FetchMethod.Get, path);
  return { ...params, get: fetchApi };
}

export function useApiPatch<T>(path: string): ApiPatchResult<T> {
  const { fetchApi, ...params } = useApiFetch<T>(FetchMethod.Patch, path);
  return { ...params, patch: fetchApi };
}

export function useApiPost<T>(path: string): ApiPostResult<T> {
  const { fetchApi, ...params } = useApiFetch<T>(FetchMethod.Post, path);
  return { ...params, post: fetchApi };
}

export function useApiPut<T>(path: string): ApiPutResult<T> {
  const { fetchApi, ...params } = useApiFetch<T>(FetchMethod.Put, path);
  return { ...params, put: fetchApi };
}

export function useApiDelete<T>(path: string): ApiDeleteResult<T> {
  const { fetchApi, ...params } = useApiFetch<T>(FetchMethod.Delete, path);
  return { ...params, del: fetchApi };
}

enum FetchActions {
  Init,
  Success,
  Failure,
  Clear
}

function fetchReducer<T>(
  state: ApiResult<T>,
  action: { type: FetchActions; payload?: T | FetchError }
): ApiResult<T> {
  switch (action.type) {
    case FetchActions.Init:
      return { ...state, isLoading: true, error: undefined };
    case FetchActions.Success:
      return { ...state, isLoading: false, data: action.payload as T };
    case FetchActions.Clear:
      return { ...state, isLoading: false, data: undefined };
    case FetchActions.Failure:
      return {
        ...state,
        isLoading: false,
        error: action.payload as FetchError
      };
    default:
      throw new Error('Unrecognized action');
  }
}

export function useApiFetch<T>(
  method: FetchMethod,
  path: string
): ApiFetchResult<T> {
  const [response, setResponse] = useState<Response>();
  const [request, setRequest] = useState<FetchRequest>();
  const [state, dispatch] = useReducer(fetchReducer, { isLoading: false });
  useEffect(() => {
    if (request) {
      const processRequest = async () => {
        if (response?.bodyUsed) {
          return; // prevent parsing request body if already parsed
        }
        dispatch({ type: FetchActions.Init });
        try {
          const result = await request.response;
          if (result.bodyUsed) return; // body already read
          const json = await result.json();
          setResponse(result);
          if (result.ok) {
            dispatch({
              type: FetchActions.Success,
              payload: parseDateFields(json)
            });
          } else {
            dispatch({ type: FetchActions.Failure, payload: json });
          }
        } catch (error) {
          if ((error as Error).name !== 'AbortError') {
            // ignore when request is aborted
            dispatch({ type: FetchActions.Failure, payload: error });
          }
        }
      };
      processRequest();
      // return request.abort; // NOTE: This causes data loss; TODO: rework abort logic
    }
  }, [request, response?.bodyUsed]);
  const fetchApi = useCallback(
    (params: ApiParams = {}) => {
      setResponse(undefined);
      // console.log('fetchApi', method, path, params);
      setRequest(api.fetch(method, path, params));
    },
    [setRequest, method, path]
  );
  const clearData = useCallback(() => {
    dispatch({ type: FetchActions.Clear });
  }, []);
  return {
    data: state.data as T,
    isLoading: state.isLoading,
    response,
    error: state.error,
    fetchApi,
    clearData
  };
}

const DATE_STRING_REGEX =
  /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z)/;
/**
 * Recursively checks the response json object for Date properties in string format and parses them to native JS Date type
 * @param json
 */
function parseDateFields(json: any): any {
  if (json === null || json === undefined) {
    return json;
  }
  if (Array.isArray(json)) {
    return json.map(item => parseDateFields(item));
  }
  if (typeof json === 'object') {
    const newJson: { [key: string]: any } = {};
    Object.keys(json).forEach((key: string) => {
      newJson[key] = parseDateFields(json[key]);
    });
    return newJson;
  }
  if (typeof json === 'string' && DATE_STRING_REGEX.test(json)) {
    return new Date(json);
  }
  return json;
}
